import { useState, useMemo, useEffect, useRef } from "react";

interface UsePaginationProps<T> {
  data: T[];
  searchTerm: string;
  searchFields: (keyof T)[];
  filters?: Record<string, any>;
  defaultRowsPerPage?: number;
}

interface UsePaginationReturn<T> {
  paginatedData: T[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  rowsPerPage: number;
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  resetPagination: () => void;
}

export function usePagination<T>({
  data,
  searchTerm,
  searchFields,
  filters = {},
  defaultRowsPerPage = 25,
}: UsePaginationProps<T>): UsePaginationReturn<T> {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const prevSearchTermRef = useRef(searchTerm);
  const prevFiltersRef = useRef(filters);

  // Filter and search data
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Apply search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        searchFields.some((field) => {
          const value = item[field];
          if (typeof value === "string") {
            return value.toLowerCase().includes(searchLower);
          }
          return false;
        })
      );
    }

    // Apply filters
    Object.entries(filters).forEach(([key, filterValue]) => {
      if (filterValue && filterValue !== "all") {
        filtered = filtered.filter((item) => {
          const itemValue = (item as any)[key];
          return itemValue === filterValue;
        });
      }
    });

    return filtered;
  }, [data, searchTerm, searchFields, filters]);

  // Calculate pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  // Get paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, rowsPerPage]);

  // Reset to page 1 when search or filters actually change
  useEffect(() => {
    const searchTermChanged = prevSearchTermRef.current !== searchTerm;
    const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
    
    if (searchTermChanged || filtersChanged) {
      setCurrentPage(1);
      prevSearchTermRef.current = searchTerm;
      prevFiltersRef.current = filters;
    }
  }, [searchTerm, filters]);

  // Reset to page 1 when rows per page changes
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const resetPagination = () => {
    setCurrentPage(1);
    setRowsPerPage(defaultRowsPerPage);
  };

  return {
    paginatedData,
    currentPage,
    totalPages,
    totalItems,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage: handleRowsPerPageChange,
    resetPagination,
  };
} 